import axios from 'axios';

// In production, use relative URLs (same domain)
// In development, use the configured API URL or default to localhost
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'production') {
    // In production, use relative URLs so it works with the reverse proxy
    return '';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:3001';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  signup: (userData) => api.post('/api/auth/signup', userData),
  getMe: () => api.get('/api/auth/me'),
  verifyEmail: (token) => api.get(`/api/auth/verify-email/${token}`),
  resendVerification: (email) => api.post('/api/auth/resend-verification', { email }),
  logout: () => {
    localStorage.removeItem('token');
    return Promise.resolve();
  }
};

export const businessApi = {
  getProfile: () => api.get('/api/business/profile'),
  updateProfile: (profileData) => api.put('/api/business/profile', profileData),
  getDashboard: () => api.get('/api/business/dashboard'),
  getPublicProfile: (businessId) => api.get(`/api/business/${businessId}/public`)
};

export const serviceApi = {
  getAll: () => api.get('/api/services'),
  getOne: (id) => api.get(`/api/services/${id}`),
  create: (serviceData) => api.post('/api/services', serviceData),
  update: (id, serviceData) => api.put(`/api/services/${id}`, serviceData),
  delete: (id) => api.delete(`/api/services/${id}`),
  toggleActive: (id) => api.put(`/api/services/${id}/toggle`)
};

export const appointmentApi = {
  getAll: (params) => api.get('/api/appointments', { params }),
  getOne: (id) => api.get(`/api/appointments/${id}`),
  create: (appointmentData) => api.post('/api/appointments', appointmentData),
  update: (id, appointmentData) => api.put(`/api/appointments/${id}`, appointmentData),
  cancel: (id) => api.delete(`/api/appointments/${id}`),
  getAvailableSlots: (params) => api.get('/api/appointments/available-slots', { params }),
  createPublic: (appointmentData) => api.post('/public/appointments', appointmentData)
};

export const availabilityApi = {
  getAllRules: () => api.get('/api/availability'),
  getOneRule: (id) => api.get(`/api/availability/${id}`),
  createRule: (ruleData) => api.post('/api/availability', ruleData),
  updateRule: (id, ruleData) => api.put(`/api/availability/${id}`, ruleData),
  deleteRule: (id) => api.delete(`/api/availability/${id}`),
  checkAvailability: (params) => api.get('/api/availability/check', { params })
};

export const notificationApi = {
  getAll: (params) => api.get('/api/notifications', { params }),
  getOne: (id) => api.get(`/api/notifications/${id}`),
  create: (notificationData) => api.post('/api/notifications', notificationData),
  update: (id, notificationData) => api.put(`/api/notifications/${id}`, notificationData),
  delete: (id) => api.delete(`/api/notifications/${id}`),
  sendTest: (email) => api.post('/api/notifications/test', { email })
};

export const publicApi = {
  searchBusinesses: (params) => api.get('/public/businesses/search', { params }),
  getBusiness: (businessId) => api.get(`/public/business/${businessId}`),
  getBusinessServices: (businessId) => api.get(`/public/business/${businessId}/services`),
  getBusinessAvailability: (businessId, params) => api.get(`/public/business/${businessId}/availability`, { params })
};

export const subscriptionApi = {
  getPlans: () => api.get('/api/subscription/plans'),
  getCurrent: () => api.get('/api/subscription/current'),
  getUsage: () => api.get('/api/subscription/usage'),
  getPaymentHistory: () => api.get('/api/subscription/payments'),
  cancel: () => api.post('/api/subscription/cancel'),
  reactivate: () => api.post('/api/subscription/reactivate'),
  createCheckout: (planId) => api.post('/api/subscription/checkout', { planId }),
  verifySession: (sessionId) => api.get(`/api/subscription/verify-session?sessionId=${sessionId}`),
  getCustomerPortal: () => api.get('/api/subscription/portal')
};

export const calendarApi = {
  getIntegrations: () => api.get('/api/calendar'),
  initiateGoogleAuth: () => api.post('/api/calendar/auth/google'),
  initiateOutlookAuth: () => api.post('/api/calendar/auth/outlook'),
  syncAppointment: (appointmentId) => api.post(`/api/calendar/sync/${appointmentId}`),
  disconnectIntegration: (integrationId) => api.delete(`/api/calendar/${integrationId}`)
};

export const analyticsApi = {
  getAnalytics: (params) => api.get('/api/analytics', { params }),
  getRevenueTrends: (params) => api.get('/api/analytics/revenue', { params })
};

export const quickbooksApi = {
  getIntegration: () => api.get('/api/quickbooks'),
  initiateAuth: () => api.post('/api/quickbooks/auth'),
  disconnect: () => api.delete('/api/quickbooks'),
  updateSyncSettings: (settings) => api.put('/api/quickbooks/settings', settings),
  testConnection: () => api.get('/api/quickbooks/test'),
  syncAppointment: (appointmentId) => api.post(`/api/quickbooks/sync/appointment/${appointmentId}`)
};

export const userApi = {
  getAll: () => api.get('/api/users'),
  create: (userData) => api.post('/api/users', userData),
  update: (id, userData) => api.put(`/api/users/${id}`, userData),
  delete: (id) => api.delete(`/api/users/${id}`),
  getMyBusiness: () => api.get('/api/users/my-business')
};

export default api;
